<script setup>

import {onMounted, ref, computed} from 'vue';
import UniversalChart from "@/components/dashboard/UniversalChart";
import DashboardWidget from "@/components/dashboard/DashboardWidget";
import ArrivalsAndDeliveries from "@/components/dashboard/ArrivalsAndDeliveries";
import WidgetContainer from "@/components/common/other/WidgetContainer";
import Notification from "@/classes/component-classes/Notification";
import UniversalDateList from "@/components/dashboard/UniversalDateList";
import MoreInfoPopOutPanel from "@/components/common/other/MoreInfoPopOutPanel";

import {useStore} from "vuex";
import {useGetImportOriginsChartData} from "@/composables/dashboard/useGetImportOriginsChartData";
import {useGetCompletedShipmentsChartData} from "@/composables/dashboard/useGetCompletedShipmentsChartData";
import {useGetDeliveriesChartData} from "@/composables/dashboard/useGetDeliveriesChartData";
import {useGetSomeChartData} from "@/composables/dashboard/useGetSomeChartData";
import {useGetSomeMoreChartData} from "@/composables/dashboard/useGetSomeMoreChartData";
import {useGetArrivalsChartData} from "@/composables/dashboard/useGetArrivalsChartData";
import {useGetRoadArrivalsChartData} from "@/composables/dashboard/useGetRoadArrivalsChartData";

import axios from "axios";
import Dashboard from "@/classes/viewmodels/Dashboard";

const store = useStore();

// TODO Chart data & date information
const arrivalsAndDeliveries = ref(new Dashboard());
const arrivalAndDeliveryDateRange = ref('');
const arrivalAndDeliveryReady = ref(false);

const completedShipments = ref([]);
const completedShipmentsDateRange = ref('');

// TODO List widget data
const arrivingToday = ref([]);
const deliveredToday = ref([]);
const lateArrival = ref([]);
const lateDelivery = ref([]);
const monthAhead = ref([]);

onMounted(() => {

  axios.get('api/Dashboard', {

    params: {
      owner: store.state.owner,
      account: store.state.userSettings.account,
    }

  }).then((response) => {

    arrivalsAndDeliveries.value.arrivals.totals.notMeasured = response.data.arrivalDeliveryTotals.arrivals.notMeasured;
    arrivalsAndDeliveries.value.arrivals.totals.early = response.data.arrivalDeliveryTotals.arrivals.early;
    arrivalsAndDeliveries.value.arrivals.totals.onTime = response.data.arrivalDeliveryTotals.arrivals.onTime;
    arrivalsAndDeliveries.value.arrivals.totals.late = response.data.arrivalDeliveryTotals.arrivals.late;

    arrivalsAndDeliveries.value.deliveries.totals.notMeasured =  response.data.arrivalDeliveryTotals.deliveries.notMeasured;
    arrivalsAndDeliveries.value.deliveries.totals.early = response.data.arrivalDeliveryTotals.deliveries.early;
    arrivalsAndDeliveries.value.deliveries.totals.onTime = response.data.arrivalDeliveryTotals.deliveries.onTime;
    arrivalsAndDeliveries.value.deliveries.totals.late = response.data.arrivalDeliveryTotals.deliveries.late;

    arrivalsAndDeliveries.value.arrivals.air.totals = response.data.arrivalDeliveryTotals.arrivals.air;
    arrivalsAndDeliveries.value.arrivals.road.totals = response.data.arrivalDeliveryTotals.arrivals.road;
    arrivalsAndDeliveries.value.arrivals.sea.totals = response.data.arrivalDeliveryTotals.arrivals.sea;
    arrivalsAndDeliveries.value.arrivals.rail.totals = response.data.arrivalDeliveryTotals.arrivals.rail;
    arrivalsAndDeliveries.value.arrivals.courier.totals = response.data.arrivalDeliveryTotals.arrivals.courier;

    arrivalsAndDeliveries.value.deliveries.air.totals = response.data.arrivalDeliveryTotals.deliveries.air;
    arrivalsAndDeliveries.value.deliveries.road.totals = response.data.arrivalDeliveryTotals.deliveries.road;
    arrivalsAndDeliveries.value.deliveries.sea.totals = response.data.arrivalDeliveryTotals.deliveries.sea;
    arrivalsAndDeliveries.value.deliveries.rail.totals = response.data.arrivalDeliveryTotals.deliveries.rail;
    arrivalsAndDeliveries.value.deliveries.courier.totals = response.data.arrivalDeliveryTotals.deliveries.courier;

    arrivalAndDeliveryDateRange.value = response.data.arrivalDeliveryTotals.from + ' - ' + response.data.arrivalDeliveryTotals.to;

    response.data.completedShipments.length? completedShipmentsDateRange.value =
        response.data.completedShipments.from + ' - ' + response.data.completedShipments.to : '';

    arrivalAndDeliveryReady.value = true;

    completedShipments.value = response.data.completedShipments.data;
    arrivingToday.value = response.data.arrivingToday;
    deliveredToday.value = response.data.deliveredToday;
    lateArrival.value = response.data.lateArrival;
    lateDelivery.value = response.data.lateDelivery;
    monthAhead.value = response.data.monthAhead;

  }).catch(function (error) {

    store.state.banners.error = new Notification(true, 'Could Not Retrieve Dashboard Data');

  });

})

const importOrigins = [

  {
    label: 'UK',
    value: 136
  },
  {
    label: 'Canada',
    value: 63
  },
  {
    label: 'France',
    value: 9
  },
  {
    label: 'US',
    value: 34
  },
  {
    label: 'Frankfurt',
    value: 12
  },
  {
    label: 'China',
    value: 80
  },

]

const completedShipmentsTemp = [

  {
    label: 'Import',
    value: 23
  },
  {
    label: 'Export',
    value: 13
  },
  {
    label: 'Cross Trade',
    value: 9
  },
  {
    label: 'Domestic',
    value: 4
  },
]

const importOriginsConfig = useGetImportOriginsChartData(importOrigins);

const completedShipmentsConfig = computed(() => {

  return useGetCompletedShipmentsChartData(completedShipments.value)

})

const displayCompletedShipments = computed(() => {

  return completedShipments.value.length > 0;

})

const tempOne = useGetSomeChartData(importOrigins);
const tempTwo = useGetSomeMoreChartData(importOrigins);

</script>

<template>
  <div style="margin-bottom: 10px"></div>
  <div class="view-dashboard">
    <WidgetContainer
        :height="200"
        :width="400"
        display="flex"
        :widget-containers="$store.state.widgetList">
      <template #arrivals>
        <DashboardWidget :height="150" :width="460">
          <template #icon>
            <i class="fa-thin fa-chart-scatter-bubble"></i>
          </template>
          <template #header-text>
            Arrivals
          </template>
          <template #header-tag-line>
            Import Arrivals & Deliveries From: <strong>{{arrivalAndDeliveryDateRange}}</strong>
          </template>
          <template #header>
          </template>
          <template #content>
            <ArrivalsAndDeliveries
                :key="arrivalAndDeliveryReady"
                v-model="arrivalsAndDeliveries.arrivals">
            </ArrivalsAndDeliveries>
          </template>
        </DashboardWidget>
        <DashboardWidget :height="150" :width="460" style="margin-top: 5px;">
          <template #icon>
          </template>
          <template #header-text>
            Deliveries
          </template>
          <template #header-tag-line>
          </template>
          <template #header>
          </template>
          <template #content>
            <ArrivalsAndDeliveries
                v-model="arrivalsAndDeliveries.deliveries">
            </ArrivalsAndDeliveries>
          </template>
        </DashboardWidget>
      </template>

      <template #import-origins>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-thin fa-earth" style="color: #216585"></i>
          </template>
          <template #header-text>
            Import Origins
          </template>
          <template #header-tag-line>
          </template>
          <template #header>
            Demo Data Only
          </template>
          <template #content>
            <UniversalChart
                v-if="importOriginsConfig.data.datasets[0].data.length > 0"
                :config="importOriginsConfig"
                chartId="import-origins">
            </UniversalChart>
          </template>
        </DashboardWidget>
      </template>

      <template #completed-shipments>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-thin fa-handshake-angle" style="color: #218532"></i>
          </template>
          <template #header-text>
            Completed Shipments
          </template>
          <template #header-tag-line>
            <strong>{{completedShipmentsDateRange}}</strong>
          </template>
          <template #header>

          </template>
          <template #content>
            <UniversalChart
                v-if="displayCompletedShipments"
                :config="completedShipmentsConfig"
                chartId="completed-shipments">
            </UniversalChart>
            <div v-else><i class="fa-kit fa-thin-circle-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>

      <template #arriving-today>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-kit fa-thin-calendar-days-circle-check" style="color: #23ac20"></i>
          </template>
          <template #header-text>
            Arriving Today
          </template>
          <template #header-tag-line>
            View Shipment Details Of Todays Arrivals
          </template>
          <template #header>
          </template>
          <template #content>
            <UniversalDateList
                v-if="arrivingToday.length > 0"
                v-model="arrivingToday">
            </UniversalDateList>
            <div v-else><i class="fa-kit fa-thin-line-columns-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>

      <template #late-arrivals>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-kit fa-thin-calendar-days-circle-xmark" style="color: #b01510"></i>
          </template>
          <template #header-text>
            Overdue Arrivals
          </template>
          <template #header-tag-line>
            View Detail Of Overdue Shipments
          </template>
          <template #header>
          </template>
          <template #content>
            <UniversalDateList
                v-if="lateArrival.length > 0"
                :arrivingToday="false"
                v-model="lateArrival">
            </UniversalDateList>
            <div v-else><i class="fa-kit fa-thin-line-columns-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>

      <template #month-ahead>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-thin fa-calendar-days" style="color: #104bb0"></i>
          </template>
          <template #header-text>
            Week / Month Ahead
          </template>
          <template #header-tag-line>
            View Future Shipment Details
          </template>
          <template #header>
          </template>
          <template #content>
            <UniversalDateList
                v-if="monthAhead.length > 0"
                :arrivingToday="false"
                :monthAhead="true"
                v-model="monthAhead">
            </UniversalDateList>
            <div v-else><i class="fa-kit fa-thin-line-columns-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>

      <template #delivered-today>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-kit fa-thin-calendar-days-circle-check" style="color: #23ac20"></i>
          </template>
          <template #header-text>
            Expected Deliveries
          </template>
          <template #header-tag-line>
            View Shipment Details Of Todays Deliveries
          </template>
          <template #header>
          </template>
          <template #content>
            <UniversalDateList
                v-if="deliveredToday.length > 0"
                v-model="deliveredToday">
            </UniversalDateList>
            <div v-else><i class="fa-kit fa-thin-line-columns-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>

      <template #late-deliveries>
        <DashboardWidget :height="305" :width="460">
          <template #icon>
            <i class="fa-kit fa-thin-calendar-days-circle-xmark" style="color: #b01510"></i>
          </template>
          <template #header-text>
            Overdue Deliveries
          </template>
          <template #header-tag-line>
            View Detail Of Overdue Deliveries
          </template>
          <template #header>
          </template>
          <template #content>
            <UniversalDateList
                v-if="lateDelivery.length > 0"
                :arrivingToday="false"
                v-model="lateDelivery">
            </UniversalDateList>
            <div v-else><i class="fa-kit fa-thin-line-columns-circle-xmark view-dashboard_placeholder-image"></i>
              <div class="view-dashboard_placeholder-text">
                <i class="fa-solid fa-database" style="color: #719ECE; font-size: 13px; margin-right: 5px"></i>No Available Data
              </div>
            </div>
          </template>
        </DashboardWidget>
      </template>
    </WidgetContainer>


    <!--      <DashboardWidget>-->
    <!--        <template #icon>-->
    <!--          <i class="fa-solid fa-truck-ramp-box" style="color: #c8b52c"></i>-->
    <!--        </template>-->
    <!--        <template #header-text>-->
    <!--          Deliveries-->
    <!--        </template>-->
    <!--        <template #header-tag-line>-->
    <!--        </template>-->
    <!--        <template #chart-header>-->
    <!--          On Time Deliveries Per Month-->
    <!--        </template>-->
    <!--        <template #chart>-->
    <!--          <UniversalChart-->
    <!--              v-if="deliveriesConfig.data.datasets[0].data.length > 0"-->
    <!--              :config="deliveriesConfig"-->
    <!--              chartId="deliveries">-->
    <!--          </UniversalChart>-->
    <!--        </template>-->
    <!--      </DashboardWidget>-->


    <!--      <DashboardWidget>-->
    <!--        <template #header-text>-->
    <!--          Bookings-->
    <!--        </template>-->
    <!--        <template #content>-->
    <!--          <div class="view-dashboard_bookings-container">-->
    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">13</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-rectangle-history-circle-plus"-->
    <!--                   style="font-size: 32px; color: #8f5d2f; margin-right: 10px;"></i> New Bookings-->
    <!--              </div>-->
    <!--              <div class="view-dashboard_tile-info-text-b">Require Notification Sending</div>-->
    <!--            </div>-->

    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">26</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-thumbs-up"-->
    <!--                   style="font-size: 32px; color: #454888; margin-right: 10px;"></i> Pending Approval-->
    <!--              </div>-->
    <!--              <div class="view-dashboard_tile-info-text-b">Notifications Sent</div>-->
    <!--            </div>-->

    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">6</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-check"-->
    <!--                   style="font-size: 32px; color: #309150; margin-right: 10px;"></i> Approved-->
    <!--              </div>-->
    <!--              <div class="view-dashboard_tile-info-text-b">Bookings into Jobs</div>-->
    <!--            </div>-->

    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">42</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-chart-network"-->
    <!--                   style="font-size: 32px; color: #918c30; margin-right: 10px;"></i> Active Shipments-->
    <!--              </div>-->
    <!--              <div class="view-dashboard_tile-info-text-b">Current Quarter</div>-->
    <!--            </div>-->
    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">3</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-pause"-->
    <!--                   style="font-size: 32px; color: #913030; margin-right: 10px;"></i> On Hold-->
    <!--              </div>-->
    <!--              <div class="view-dashboard_tile-info-text-b">Require Attention!</div>-->
    <!--            </div>-->
    <!--            <div class="view-dashboard_tile-layout-b">-->
    <!--              <div class="view-dashboard_data-value-b">89</div>-->
    <!--              <div>-->
    <!--                <i class="fa-regular fa-message-question"-->
    <!--                   style="font-size: 32px; color: #8a8a8a; margin-right: 10px;"></i> Information Slot-->
    <!--              </div>-->
    <!--            </div>-->
    <!--          </div>-->
    <!--        </template>-->
    <!--      </DashboardWidget>-->

    <!--      <DashboardWidget>-->
    <!--        <template #icon>-->
    <!--          <i class="fa-solid fa-chart-waterfall" style="color: #216585"></i>-->
    <!--        </template>-->
    <!--        <template #header-text>-->
    <!--          More Chart Types-->
    <!--        </template>-->
    <!--        <template #header-tag-line>-->
    <!--        </template>-->
    <!--        <template #chart-header>-->
    <!--          Top Origins By Number Of Jobs Per Month-->
    <!--        </template>-->
    <!--        <template #chart>-->
    <!--          <UniversalChart-->
    <!--              v-if="tempOne.data.datasets[0].data.length > 0"-->
    <!--              :config="tempOne"-->
    <!--              chartId="imports">-->
    <!--          </UniversalChart>-->
    <!--        </template>-->
    <!--      </DashboardWidget>-->

<!--          <DashboardWidget>-->
<!--            <template #icon>-->
<!--              <i class="fa-solid fa-chart-waterfall" style="color: #216585"></i>-->
<!--            </template>-->
<!--            <template #header-text>-->
<!--              Even More Chart Types-->
<!--            </template>-->
<!--            <template #header-tag-line>-->
<!--            </template>-->
<!--            <template #chart-header>-->
<!--              Top Origins By Number Of Jobs Per Month-->
<!--            </template>-->
<!--            <template #chart>-->
<!--              <UniversalChart-->
<!--                  v-if="tempTwo.data.datasets[0].data.length > 0"-->
<!--                  :config="tempTwo"-->
<!--                  chartId="leavers">-->
<!--              </UniversalChart>-->
<!--            </template>-->
<!--          </DashboardWidget>-->


    <!--      <div class="view-dashboard_bookings-section">-->
    <!--        <div class="view-dashboard_tile-layout">-->
    <!--          <div class="view-dashboard_data-area">-->
    <!--            <div class="view-dashboard_data-value">-->
    <!--              43-->
    <!--            </div>-->
    <!--          </div>-->
    <!--          <div class="view-dashboard_tile-label">-->
    <!--            <i class="fa-regular fa-calendar-check" style="font-size: 20px; color: #566ea0; margin-right: 10px;"></i> Pending Approval-->
    <!--          </div>-->
    <!--        </div>-->
    <!--        <div class="view-dashboard_tile-layout">-->
    <!--          <div class="view-dashboard_data-area">-->
    <!--            <div class="view-dashboard_data-value">-->
    <!--              14-->
    <!--            </div>-->
    <!--          </div>-->
    <!--          <div class="view-dashboard_tile-label">-->
    <!--            <i class="fa-regular fa-book-open" style="font-size: 20px; color: #569aa0; margin-right: 10px;"></i> Open Requests-->
    <!--          </div>-->
    <!--        </div>-->

    <!--        <div class="view-dashboard_tile-layout">-->
    <!--          <div class="view-dashboard_data-area">-->
    <!--            <div class="view-dashboard_data-value">-->
    <!--              27-->
    <!--            </div>-->
    <!--          </div>-->
    <!--          <div class="view-dashboard_tile-label">-->
    <!--            <i class="fa-regular fa-circle-check" style="font-size: 20px; color: #56a082; margin-right: 10px;"></i> Approved-->
    <!--          </div>-->
    <!--        </div>-->

    <!--        <div class="view-dashboard_tile-layout">-->
    <!--          <div class="view-dashboard_data-area">-->
    <!--            <div class="view-dashboard_data-value">-->
    <!--              6-->
    <!--            </div>-->
    <!--          </div>-->
    <!--          <div class="view-dashboard_tile-label">-->
    <!--            <i class="fa-regular fa-ballot-check" style="font-size: 20px; color: #295a0d; margin-right: 10px;"></i> Confirmed-->
    <!--          </div>-->
    <!--        </div>-->

    <!--        <div class="view-dashboard_tile-layout">-->
    <!--          <div class="view-dashboard_data-area">-->
    <!--            <div class="view-dashboard_data-value">-->
    <!--              4-->
    <!--            </div>-->
    <!--          </div>-->
    <!--          <div class="view-dashboard_tile-label">-->
    <!--            <i class="fa-regular fa-pause" style="font-size: 20px; color: #a05656; margin-right: 10px;"></i> On Hold-->
    <!--          </div>-->
    <!--        </div>-->
    <!--      </div>-->

  </div>


</template>

<style lang="scss" scoped>

@import '@/css/dashboard/info-container-type-a.scss';
@import '@/css/dashboard/info-container-type-b.scss';

.view-dashboard {
  max-width: 1500px;
  margin: auto;
  height: 800px;
  overflow-y: auto;

  &_bookings-wrapper {
    margin-top: 30px;
    gap: 30px;
  }

  &_top-section, &_bottom-section {
    gap: 30px;
  }

  &_bottom-section {
    margin-top: 10px;
  }

  &_bookings-title {
    padding: 5px;
    border-bottom: 1px solid var(--border-color);
    //background-image: linear-gradient(0deg, #eaeaea, #ffffff);
  }

  &_placeholder-image {
    font-size: 160px;
    color: #cde8ea;
    margin-top: 20px;
  }

  &_placeholder-image-small {
    font-size: 60px;
    color: #cde8ea;
    margin-left: 20px;
  }

  &_placeholder-text {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 13px;
    color: #404040;
    margin-top: 10px;
    padding: 5px 0 5px 0;
  }

  &_due-today-date-color {
    height: 10px;
    width: 20px;
    border-radius: 3px;
    margin: 0 5px 0 20px;
    background: var(--grid-cell-date-due-today);
  }

  &_arrivals-header {
    display: grid;
    grid-template-columns: 93px 60px 82px 50px;
    width: 100%;
    margin-left: 27px;
    gap: 30px;
    font-size: 13px;
    font-weight: bold;
  }

  &_arrivals-data-circles {
    display: grid;
    grid-template-columns: 100px 100px 100px 100px;
    margin: 10px 0  0 35px;
  }

  &_circle {
    display: flex;
    font-size: 13px;
    height: 54px;
    width: 54px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 2px 4px 0px #bfbfbf;
  }

  &_circle-not-measured {
    border: 4px solid #3e3e3e9c;
  }

  &_circle-early {
    border: 4px solid #f8c8029c;
  }

  &_circle-on-time {
    border: 4px solid #11bf289c
  }

  &_circle-late {
    border: 4px solid #e00d0d80;
  }

  &_arrivals-mode-layout {
    margin: -10px;
    background: #0a0a0a;
    height: auto;
    width: 200px;
    border-radius: 6px;
  }

  &_tooltip-wrapper {
    padding: 10px;
    color: #fcfcfc;
  }

  &_not-measured-block {
    height: 15px;
    width: 15px;
    background: #3e3e3e9c;
    border: 1px solid #8a8a8a;
  }

  &_tooltip-data {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }
}

</style>
